/*
  Video.js Download plugin (TypeScript)
  Migrated from src/assets/js/videojs-download/videojs-download.js
  Registers a control bar button with a dropdown menu of downloadable qualities.
*/

import videojs from "video.js";
declare const download: any;

interface DownloadMediaQualityOption {
  codec: string;
  resolution: string;
  quality?: string;
}

interface OriginalMediaInfo {
  resolution: string;
  codec: string;
}

export interface VideoJsDownloadOptions {
  beforeElement?: string;
  textControl?: string;
  name?: string;
  downloadURL?: string | null;
  controlId?: string;
  isDownloading?: boolean;
  downloadCallback?: (controlId: string, quality?: string) => void;
  mediaQuality?: DownloadMediaQualityOption[];
  originalMedia?: OriginalMediaInfo;
}

const defaultOptions: Required<Pick<VideoJsDownloadOptions, 'beforeElement' | 'textControl' | 'name'>> &
  Omit<VideoJsDownloadOptions, 'beforeElement' | 'textControl' | 'name'> = {
  beforeElement: 'fullscreenToggle',
  textControl: 'Download video',
  name: 'downloadButton',
  downloadURL: null,
  controlId: undefined,
  isDownloading: false,
  downloadCallback: undefined,
  mediaQuality: [],
  originalMedia: { resolution: '', codec: '' }
};

const VjsButton = videojs.getComponent('Button') as any;

class DownloadButton extends VjsButton {
  private readonly options_: VideoJsDownloadOptions;
  private documentClickHandler?: (e: Event) => void;

  constructor(player: any, options: VideoJsDownloadOptions) {
    super(player, options);
    this.options_ = options || {};

    const menu = document.createElement('ul');
    menu.classList.add('vjs-download-menu');

    const menuLabel = document.createElement('li');
    menuLabel.innerText = 'Original';
    menuLabel.classList.add('menu-label');
    menu.append(menuLabel);

    const originalDownload = document.createElement('li');
    const codec = this.options_.originalMedia?.codec || '';
    const resolution = this.options_.originalMedia?.resolution || '';
    originalDownload.innerText = `${codec.toUpperCase()} ${resolution}`.trim();
    originalDownload.classList.add('original-file');
    originalDownload.classList.add('downloadable-file');
    originalDownload.addEventListener('click', (e: Event) => {
      e.preventDefault();
      if (typeof this.options_.downloadCallback === 'function') {
        this.options_.downloadCallback(this.options_.controlId || '');
      } else {
        const url = (this.options_.downloadURL || player.currentSrc());
        download(url, undefined, { control: 'button.vjs-vjsdownload' });
      }
    });
    menu.append(originalDownload);

    if (Array.isArray(this.options_.mediaQuality) && this.options_.mediaQuality.length) {
      const lowerQualityLabel = document.createElement('li');
      lowerQualityLabel.innerText = 'Lower quality';
      lowerQualityLabel.classList.add('lower-quality-file');
      menu.append(lowerQualityLabel);
    }

    (this.options_.mediaQuality || []).forEach((q, idx) => {
      const item = document.createElement('li');
      item.innerText = `${q.codec} ${q.resolution}`;
      item.classList.add('downloadable-file');
      item.addEventListener('click', (e: Event) => {
        e.preventDefault();
        if (typeof this.options_.downloadCallback === 'function') {
          this.options_.downloadCallback(this.options_.controlId || '', q.quality);
        } else {
          const url = (this.options_.downloadURL || player.currentSrc());
          download(url, undefined, { control: 'button.vjs-vjsdownload' });
        }
      });
      menu.append(item);
    });

    this.el_?.append(menu);

    // Toggle dropdown without relying on jQuery
    this.el_?.addEventListener('click', (event: Event) => {
      // Ignore when already processing a download
      if (this.el_?.classList.contains('downloading')) return;
      this.el_?.classList.toggle('vjs-menu-open');
      event.stopPropagation();
    });

    // Close menu when clicking outside
    this.documentClickHandler = (e: Event) => {
      const target = e.target as HTMLElement;
      const el = this.el_;
      if (el && !el.contains(target)) {
        el.classList.remove('vjs-menu-open');
      }
    };
    document.addEventListener('click', this.documentClickHandler);
  }

  buildCSSClass(): string {
    return `vjs-vjsdownload ${super.buildCSSClass()}`;
  }

  dispose() {
    // Clean up document event listener
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
      this.documentClickHandler = undefined;
    }
    // Call parent dispose method
    super.dispose?.();
  }

  handleClick(): void {
    // Intentionally handled via explicit listener above to toggle menu
    if (this.el_?.classList.contains('downloading')) return;
  }
}

function onPlayerReady(player: any, options: VideoJsDownloadOptions): void {
  const dButton = player.controlBar.addChild(new DownloadButton(player, options), {});

  if (options.controlId) {
    dButton.el_.setAttribute('id', options.controlId);
  }
  if (options.isDownloading) {
    dButton.el_.classList.add('downloading');
  }

  dButton.controlText(options.textControl || defaultOptions.textControl);

  // Place download button to the left of the resolution switcher (if present), otherwise before PiP, else append
  const barEl = player.controlBar.el();
  const pipChild = player.controlBar.getChild('PictureInPictureToggle');
  const resEl = barEl.querySelector('.vjs-resolution-button');
  const downloadEl = dButton.el();
  if (resEl && resEl.parentNode === barEl) {
    // Insert download before resolution button
    barEl.insertBefore(downloadEl, resEl as any);
  } else if (pipChild && typeof (pipChild as any).el === 'function') {
    barEl.insertBefore(downloadEl, (pipChild as any).el());
  } else {
    barEl.appendChild(downloadEl);
  }

  player.addClass('vjs-vjsdownload');
}

export function vjsdownload(opts?: VideoJsDownloadOptions): void {
  const merged = (videojs.mergeOptions ? videojs.mergeOptions(defaultOptions, opts || {}) : { ...defaultOptions, ...(opts || {}) }) as VideoJsDownloadOptions;
  const player = this;
  this.ready(function () {
    onPlayerReady(player, merged);
  });
}

videojs.registerPlugin('vjsdownload', vjsdownload);

export default vjsdownload;

